import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  TrafficHistogramApi,
  type HistogramEntry,
} from '@/services/stats/trafficHistogramApi'
import type { Targeting } from '@/services/targeting/targeting'
import { COUNTRIES } from '@/data/countries'

/**
 * The endpoint reports on one site per request, so a line item targeting
 * several costs one request each. Capped so a broad target does not fan out.
 */
const MAX_SITES = 10

/**
 * The histogram's COUNTRY filter matches on the country's full name, e.g.
 * "Norway", while the targeting holds ISO codes. A code with no name of its own
 * is passed through unchanged rather than dropped.
 */
function countryNames(codes: string[]): string[] {
  return codes.map((code) => COUNTRIES.find((c) => c.value === code)?.label ?? code)
}

interface SizeRow {
  size: string
  /**
   * Requests expected over the coming week, summed across the sites asked
   * about. Not shown, but it is what the shares and the ordering come from.
   */
  expectedNextWeek: number
  /** Share of the traffic counted, 0–1. */
  share: number
}

/**
 * Merges the per-site histograms into one list. Frequencies are each relative
 * to their own request, so the shares are recomputed from the expected request
 * counts, which do add up.
 */
function mergeSizes(responses: HistogramEntry[][]): SizeRow[] {
  const totals = new Map<string, number>()
  for (const histogram of responses) {
    for (const entry of histogram) {
      totals.set(entry.value, (totals.get(entry.value) ?? 0) + entry.expectedNextWeek)
    }
  }

  const grandTotal = [...totals.values()].reduce((sum, n) => sum + n, 0)
  if (grandTotal === 0) {
    // No forecast to divide up — fall back to the frequencies as they came.
    const shares = new Map<string, number>()
    for (const histogram of responses) {
      for (const entry of histogram) {
        shares.set(entry.value, (shares.get(entry.value) ?? 0) + entry.frequency)
      }
    }
    const count = Math.max(responses.length, 1)
    return [...shares.entries()]
      .map(([size, share]) => ({ size, expectedNextWeek: 0, share: share / count }))
      .sort((a, b) => b.share - a.share)
  }

  return [...totals.entries()]
    .map(([size, expectedNextWeek]) => ({
      size,
      expectedNextWeek,
      share: expectedNextWeek / grandTotal,
    }))
    .sort((a, b) => b.expectedNextWeek - a.expectedNextWeek)
}

/**
 * What the line item's targeting is expected to reach, by creative size. The
 * check is a forecast over the whole of the targeted inventory, not something
 * to pay for on every page load, so it only runs once `enabled` — when the
 * sheet is first opened.
 */
export function useInventoryCoverage(
  targeting: Targeting,
  creativeSizes: string[],
  enabled: boolean
) {
  const covered = useMemo(
    () => new Set(creativeSizes.map((size) => size.toLowerCase())),
    [creativeSizes]
  )

  const sites = (targeting.siteTarget?.sites ?? []).map((site) => site.id).filter(Boolean)
  const countries = targeting.siteCountryTarget?.countries ?? []
  // Sites are the narrower target, so they win; countries stand in otherwise.
  const bySite = sites.length > 0
  // Without sites or countries there is nothing to scope the check to, and a
  // network-wide forecast says nothing about this line item.
  const canCheck = sites.length > 0 || countries.length > 0

  const query = useQuery({
    queryKey: ['inventorySizes', sites, countries],
    queryFn: async () => {
      const histogramsOf = (response: Awaited<ReturnType<typeof TrafficHistogramApi.get>>) =>
        response.CREATIVE_SIZE?.histogram ?? []

      if (bySite) {
        const responses = await Promise.all(
          sites.slice(0, MAX_SITES).map((site) =>
            TrafficHistogramApi.get({ types: ['CREATIVE_SIZE'], site })
          )
        )
        return mergeSizes(responses.map(histogramsOf))
      }

      const response = await TrafficHistogramApi.get({
        types: ['CREATIVE_SIZE'],
        ...(countries.length > 0 ? { filter: { COUNTRY: countryNames(countries) } } : {}),
      })
      return mergeSizes([histogramsOf(response)])
    },
    enabled: enabled && canCheck,
    staleTime: 5 * 60 * 1000,
  })

  /** How much of the forecast traffic the line item's creatives can fill. */
  const coveredShare = (query.data ?? [])
    .filter((row) => covered.has(row.size.toLowerCase()))
    .reduce((sum, row) => sum + row.share, 0)

  return {
    ...query,
    covered,
    coveredShare,
    canCheck,
    bySite,
    siteCount: sites.length,
    countryCount: countries.length,
    hasCreatives: creativeSizes.length > 0,
  }
}

export type InventoryCoverage = ReturnType<typeof useInventoryCoverage>

/** Which sites or countries the check covers, as a sentence. */
export function inventoryScopeKey(coverage: InventoryCoverage): [string, { count: number }?] {
  if (coverage.bySite) {
    return ['inventory.scopeSites', { count: Math.min(coverage.siteCount, MAX_SITES) }]
  }
  if (coverage.countryCount > 0) {
    return ['inventory.scopeCountries', { count: coverage.countryCount }]
  }
  return ['inventory.noTargeting']
}

export interface InventoryPanelProps {
  coverage: InventoryCoverage
}

/** The creative sizes the targeting reaches, and how much of it the creatives cover. */
export function InventoryPanel({ coverage }: InventoryPanelProps) {
  const { t } = useTranslation()
  const { data, isFetching, error, covered, coveredShare, siteCount, hasCreatives } = coverage
  const [scopeKey, scopeOptions] = inventoryScopeKey(coverage)

  return (
        <div className="flex max-h-[28rem] flex-col gap-3 overflow-y-auto">
          <p className="text-sm text-(--ad-text-muted)">
            {t('inventory.description')} {t(scopeKey, scopeOptions)}
          </p>

          {siteCount > MAX_SITES && (
            <Alert>
              <TriangleAlert className="size-4" />
              <AlertDescription>
                {t('inventory.siteLimit', { count: MAX_SITES, total: siteCount })}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <TriangleAlert className="size-4" />
              <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
            </Alert>
          )}

          {isFetching && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {!isFetching && data && data.length === 0 && (
            <p className="text-sm text-(--ad-text-muted)">{t('inventory.noResults')}</p>
          )}

          {!isFetching && data && data.length > 0 && (
            <>
              <p className="text-sm">
                {hasCreatives ? (
                  <>
                    <span
                      className="font-medium tabular-nums"
                      style={{ color: 'var(--ad-success)' }}
                    >
                      {(coveredShare * 100).toFixed(1)}%
                    </span>{' '}
                    <span className="text-(--ad-text-muted)">{t('inventory.coveredSummary')}</span>
                  </>
                ) : (
                  <span className="text-(--ad-text-muted)">{t('inventory.noCreatives')}</span>
                )}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('inventory.size')}</TableHead>
                    <TableHead className="text-right">{t('inventory.share')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row) => (
                    <TableRow
                      key={row.size}
                      // A size the line item already has a creative for.
                      style={
                        covered.has(row.size.toLowerCase())
                          ? { background: 'rgba(var(--ad-success-rgb), 0.12)' }
                          : undefined
                      }
                      title={
                        covered.has(row.size.toLowerCase()) ? t('inventory.covered') : undefined
                      }
                    >
                      <TableCell className="font-medium">{row.size}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(row.share * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
  )
}
